import { useMemo, useId } from 'react';
import { AdminFilterDropdown } from '../../molecules/AdminFilters/Dropdown';
import { useDropdownState } from '../../../common/hooks/useDropdownState';
import { styled, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

type Props = {
    // eslint-disable-next-line
    onChangeItemsPerPage(itemsPerPage: number): void;
    // eslint-disable-next-line
    onChangePage(page: number): void;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
};

export const Pagination = ({
    onChangeItemsPerPage,
    onChangePage,
    totalPages,
    currentPage = 1,
    itemsPerPage,
    totalItems
}: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    // TODO: Improve this logic
    const pagesList = useMemo(() => {
        const pages: number[] = [];

        if (totalPages < 1) {
            return pages;
        }

        if (currentPage === totalPages && currentPage > 3) {
            pages.push(currentPage - 2);
        }

        if (currentPage > 1) {
            pages.push(currentPage - 1);
        }

        pages.push(currentPage);

        if (currentPage < totalPages) {
            pages.push(currentPage + 1);
        }

        if (currentPage === 1 && totalPages > 2) {
            pages.push(3);
        }

        return pages;
    }, [currentPage, totalPages]);

    const firstItemIndex = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const lastItemIndex = useMemo(() => {
        const index = itemsPerPage * currentPage;

        if (index > totalItems) {
            return totalItems;
        }

        return index;
    }, [itemsPerPage, currentPage, totalItems]);

    const { toggle, isActive, setAnchorEl, ...dropdownProps } = useDropdownState();
    const listboxId = useId();
    const rowsPerPageLabelId = useId();

    return (
        <>
            <Container>
                <Text id={rowsPerPageLabelId} style={{ marginRight: 8 }}>
                    {t('ROWS_PER_PAGE')}
                </Text>{' '}
                <NumberPerPage
                    type="button"
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isActive}
                    aria-controls={listboxId}
                    aria-labelledby={rowsPerPageLabelId}
                    onClick={toggle}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            if (!isActive) {
                                setAnchorEl(e.currentTarget);
                            }
                        } else if (e.key === 'Escape') {
                            setAnchorEl(null);
                        }
                    }}
                >
                    <Text>{itemsPerPage}</Text>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 10.25L3.25 4.25L10.75 4.25L7 10.25Z" fill={theme.palette.semantic.icon.brand} />
                    </svg>
                </NumberPerPage>
                <Text style={{ margin: '0 24px' }}>
                    {firstItemIndex}-{lastItemIndex} {t('OF_TEXT_SMALL')} {totalItems}
                </Text>
                <PageNumberContainer>
                    {pagesList.map((page, i) => (
                        <PageNumber
                            key={`${page}_${i}`}
                            type="button"
                            isActive={page === currentPage}
                            onClick={() => onChangePage(page)}
                        >
                            {page}
                        </PageNumber>
                    ))}
                </PageNumberContainer>
            </Container>
            <AdminFilterDropdown
                {...dropdownProps}
                options={[
                    {
                        value: 10,
                        label: '10'
                    },
                    {
                        value: 20,
                        label: '20'
                    },
                    {
                        value: 30,
                        label: '30'
                    }
                ]}
                width={40}
                onSelectOption={({ value }) => {
                    onChangeItemsPerPage(value);
                    toggle();
                }}
                selectedOption={itemsPerPage}
                listboxId={listboxId}
                listboxLabel={t('ROWS_PER_PAGE')}
            />
        </>
    );
};

const Container = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center'
}));

const Text = styled('span')(({ theme }) => ({
    color: theme.palette.semantic.text.brand,
    padding: 0,
    margin: 0,
    fontSize: '16px',
    lineHeight: '20px'
}));

const NumberPerPage = styled('button')(({}) => ({
    width: '37px',
    height: '20px',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    border: 'none',
    padding: 0,
    gap: '4px',
    cursor: 'pointer',
    top: '1px',
    justifyContent: 'space-between',
    zIndex: 1,
    marginTop: '1px'
}));

const PageNumberContainer = styled('div')(({}) => ({
    display: 'flex',
    gap: '12px'
}));

const PageNumber = styled('button')<{ isActive?: boolean }>(({ isActive = false, theme }) => ({
    background: isActive ? theme.palette.semantic.int.selected : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 12px',
    color: `${isActive ? theme.palette.semantic.text.inverse : theme.palette.semantic.text.brand}!important`,
    fontWeight: isActive ? 700 : 500,
    fontSize: '16px',
    lineHeight: '20px',
    cursor: 'pointer',
    ':hover': {
        background: theme.palette.neutral[300],
        color: `${theme.palette.semantic.text.brand}!important`
    }
}));
